import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { checkInStudents, checkOutStudents } from "../../lib/api";
import { printPage } from "../../lib/print";
import { shortPlanWindow, scheduleWindowNote } from "../../lib/altPlans";

export function AttendanceSummary({
  selectedDate, getSortedStudents, getStudentAttendance, displayName,
  handleStatusChange, fetchData, alternatePlans = [],
}) {
  // Get day of week from selectedDate
  const dayOfWeek = (() => {
    const d = new Date(selectedDate + "T00:00:00");
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return days[d.getDay()];
  })();

  const getAltPlanNote = (studentId) => {
    const plan = alternatePlans.find(p => p.student_id === studentId && p.days.includes(dayOfWeek) && p.active !== false);
    if (!plan) return null;
    const win = shortPlanWindow(plan);
    return win ? `${plan.destination} (${win})` : plan.destination;
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Attendance Summary for {selectedDate}</CardTitle>
          <p className="text-sm text-muted-foreground">Click on a status or time to edit</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          const printEl = document.getElementById('attendance-summary-print');
          if (!printEl) return;
          printPage({
            title: `Attendance Summary — ${selectedDate}`,
            content: printEl.innerHTML,
            extraStyles: `th { background: #00305E !important; color: #fff !important; }`,
          });
        }} data-testid="print-attendance-summary-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Print / Share
        </Button>
      </CardHeader>
      <CardContent>
        <div id="attendance-summary-print">
          <h2 style={{display:'none'}}>SMARTIS Attendance - {selectedDate}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-summary-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3 font-medium text-xs">Student</th>
                  <th className="text-left py-2 px-3 font-medium text-xs">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-xs">In</th>
                  <th className="text-left py-2 px-3 font-medium text-xs">Out</th>
                  <th className="text-left py-2 px-3 font-medium text-xs">Notes</th>
                </tr>
              </thead>
              <tbody>
                {getSortedStudents().filter(s => !s.start_date || s.start_date <= selectedDate).map((student) => {
                  const att = getStudentAttendance(student.id);
                  const currentStatus = att?.status || att?.absence_type || "";
                  const isUnmarked = !currentStatus;
                  return (
                    <tr key={student.id} className={`border-b hover:bg-gray-50/50 ${isUnmarked ? 'bg-yellow-50/50' : ''}`}>
                      <td className="py-2 px-3 font-medium text-sm">
                        {displayName(student)} {student.last_name}
                        {isUnmarked && <span className="ml-2 text-[10px] text-amber-600 font-medium">(Not marked)</span>}
                      </td>
                      <td className="py-1.5 px-3">
                        <Select value={currentStatus} onValueChange={(value) => handleStatusChange(student.id, value)}>
                          <SelectTrigger className={`w-[140px] h-7 text-xs ${
                            currentStatus === "present" ? "bg-emerald-100 border-emerald-300 text-emerald-800" :
                            currentStatus === "absent_excused" || currentStatus === "excused" ? "bg-blue-100 border-blue-300 text-blue-800" :
                            currentStatus === "absent_unexcused" || currentStatus === "unexcused" || currentStatus === "absent" ? "bg-red-100 border-red-300 text-red-800" :
                            currentStatus === "tardy" ? "bg-amber-100 border-amber-300 text-amber-800" :
                            currentStatus === "on_pause" ? "bg-gray-100 border-gray-300 text-gray-800" :
                            currentStatus === "left_early_unexcused" ? "bg-orange-100 border-orange-300 text-orange-800" : "bg-gray-50"
                          }`} data-testid={`status-select-${student.id}`}>
                            <SelectValue placeholder="Set status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Present</span></SelectItem>
                            <SelectItem value="tardy"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Tardy</span></SelectItem>
                            <SelectItem value="absent_excused"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span>Absent Excused</span></SelectItem>
                            <SelectItem value="absent_unexcused"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span>Absent Unexcused</span></SelectItem>
                            <SelectItem value="on_pause"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-gray-500"></span>On Pause</span></SelectItem>
                            <SelectItem value="left_early_unexcused"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500"></span>Left Early</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1.5 px-3">
                        <Input type="time" value={att?.check_in_time || ""} className="w-[100px] h-7 text-xs"
                          data-testid={`edit-checkin-${student.id}`}
                          onChange={async (e) => {
                            try {
                              await checkInStudents([{ student_id: student.id, check_in_time: e.target.value, status: currentStatus || "present" }], selectedDate);
                              fetchData();
                            } catch { toast.error("Failed to update time"); }
                          }} />
                      </td>
                      <td className="py-1.5 px-3">
                        <Input type="time" value={att?.check_out_time || ""} className="w-[100px] h-7 text-xs"
                          data-testid={`edit-checkout-${student.id}`}
                          onChange={async (e) => {
                            try {
                              await checkOutStudents([{ student_id: student.id, check_out_time: e.target.value }], selectedDate);
                              fetchData();
                            } catch { toast.error("Failed to update time"); }
                          }} />
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {(() => {
                          const schedNote = scheduleWindowNote(student, dayOfWeek);
                          const altNote = getAltPlanNote(student.id);
                          const attNote = att?.notes;
                          return (
                            <span className="flex flex-wrap items-center gap-1">
                              {schedNote && (
                                <span
                                  className="inline-block rounded bg-indigo-100 text-indigo-800 border border-indigo-200 px-1.5 py-0.5 font-medium"
                                  data-testid={`schedule-window-note-${student.id}`}
                                >
                                  {schedNote}
                                </span>
                              )}
                              {altNote && <span className="text-amber-700 font-medium">{altNote}</span>}
                              {attNote && <span className="text-muted-foreground">{(schedNote || altNote) ? `| ${attNote}` : attNote}</span>}
                              {!schedNote && !altNote && !attNote && <span className="text-muted-foreground">-</span>}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
